/**
 * 처방 등록 시 PDF를 페이지별 PNG로 변환한다.
 * 레거시 `convertPdfToPngPages` + AddPrescriptionModal onChange와 동일하게
 * 원본 PDF는 올리지 않고 `{이름}_page_{n}.png`만 presigned 업로드한다.
 */
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import pdfWorkerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

export type PdfPageConvertFn = (file: File, scale?: number) => Promise<File[]>;

export type ExpandPrescriptionUploadResult = {
  files: File[];
  converted: { name: string; pageCount: number }[];
  failed: { name: string; message: string }[];
};

const canvasToPngBlob = (canvas: HTMLCanvasElement): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob
          ? resolve(blob)
          : reject(new Error('PNG Blob 변환에 실패했습니다.')),
      'image/png',
      1.0,
    );
  });

const ensurePdfWorker = () => {
  if (!GlobalWorkerOptions.workerSrc) {
    GlobalWorkerOptions.workerSrc = pdfWorkerSrc;
  }
};

export const isPdfUploadFile = (file: File): boolean =>
  file.type === 'application/pdf' || /\.pdf$/i.test(file.name);

/**
 * PDF를 페이지 단위 PNG File 배열로 변환한다.
 * scale은 레거시와 같이 1~3으로 클램프하며 기본값은 2다.
 */
export const convertPdfToPngPages: PdfPageConvertFn = async (
  pdfFile,
  scale = 2,
) => {
  if (!(pdfFile instanceof File)) {
    throw new Error('유효하지 않은 PDF 파일입니다.');
  }

  const safeScale = Math.min(Math.max(scale, 1), 3);
  ensurePdfWorker();

  try {
    const data = new Uint8Array(await pdfFile.arrayBuffer());
    const loadingTask = getDocument({ data });
    const pdf = await loadingTask.promise;
    const baseName = pdfFile.name.replace(/\.pdf$/i, '');
    const pngFiles: File[] = [];

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { alpha: false });

    try {
      if (!ctx) {
        throw new Error('Canvas context를 생성할 수 없습니다.');
      }

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale: safeScale });

        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        await page.render({
          canvas,
          canvasContext: ctx,
          viewport,
        }).promise;

        const blob = await canvasToPngBlob(canvas);
        pngFiles.push(
          new File([blob], `${baseName}_page_${pageNum}.png`, {
            type: 'image/png',
            lastModified: Date.now(),
          }),
        );
        page.cleanup();
      }
    } finally {
      canvas.width = 0;
      canvas.height = 0;
      await pdf.cleanup();
      await loadingTask.destroy();
    }

    return pngFiles;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`PDF 변환 실패: ${message}`);
  }
};

/**
 * 선택한 파일 중 PDF는 PNG 페이지로 바꾸고, 이미지는 그대로 둔다.
 * 변환 실패한 PDF는 목록에서 빼서 원본 PDF가 올라가지 않게 한다.
 */
export const expandPrescriptionUploadFiles = async (
  files: File[],
  convert: PdfPageConvertFn = convertPdfToPngPages,
): Promise<ExpandPrescriptionUploadResult> => {
  const next: File[] = [];
  const converted: ExpandPrescriptionUploadResult['converted'] = [];
  const failed: ExpandPrescriptionUploadResult['failed'] = [];

  for (const file of files) {
    if (!isPdfUploadFile(file)) {
      next.push(file);
      continue;
    }

    try {
      const pages = await convert(file);
      converted.push({ name: file.name, pageCount: pages.length });
      next.push(...pages);
    } catch (error) {
      failed.push({
        name: file.name,
        message:
          error instanceof Error ? error.message : 'PDF 변환에 실패했습니다.',
      });
    }
  }

  return { files: next, converted, failed };
};
